import { useQuery } from '@tanstack/react-query';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import {
  ChatSocketMessageType,
  IAgentConversationDataModel,
  IAgentMessageDataModel,
} from '../../../schemas/agent-conversation.schema';
import { queryClient } from '../../../services/MaggieQueryClient';
import {
  deleteAgentConversation,
  getAllAgentConversations,
  getConversationMessages,
} from '../../../services/queries/AgentQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { sourcingSearchPromptAtom } from '../../Sourcing/state/SourcingStateJ';
import { MessageData } from '../components/Message';
import { AgentActivityAtom } from './agentActivityAtom';

export const ChatMessagesAtom = atom<MessageData[]>([]);
export const ChatConversationIdAtom = atom<number | undefined>();
/** One-shot opening question a page can queue for the assistant (today: the
 *  Atlas console greets with an Atlas status ask). Consumed by the ChatBot via
 *  useConsumeSeedChatPrompt. */
export const SeedChatPromptAtom = atom<string | null>(null);
export const ShowChatMenuAtom = atom(false);
export const IsReceivingMessagesAtom = atom(false);
export const ChatErrorStateAtom = atom<string | null>(null);
export const IsCancellingChatGenerationAtom = atom(false);
/** Identity the agent reported for the live WS connection (from the
 *  `connection_established` frame): the user and the tenant the socket was
 *  bound to, the customer tenant when it was known at connect time and the
 *  token's tenant otherwise — see useAgentTenantMismatch. */
export const AgentWsIdentityAtom = atom<{ userId: string; tenantId?: string } | null>(null);

export const ChatConversationsQueryKey = 'chat/conversations';
export function useConversations() {
  return useQuery<IAgentConversationDataModel[]>({
    queryKey: [ChatConversationsQueryKey],
    queryFn: getAllAgentConversations,
  });
}

export function useGetConversation() {
  const { pushErrorToast } = useToastMessageState();
  return useCallback(
    async (conversationId: number) => {
      try {
        const data = await getConversationMessages(conversationId);
        return removeEmptyAgentMessages(data);
      } catch (error) {
        console.error(error);
        pushErrorToast({ message: getErrorMessage(error, 'Failed to get conversation') });
      }
    },
    [pushErrorToast]
  );
}

function removeEmptyAgentMessages(messages: IAgentMessageDataModel[]): IAgentMessageDataModel[] {
  return messages.filter((message) => message.content !== '');
}

export function useDeleteConversation() {
  const { pushErrorToast } = useToastMessageState();
  const onCreateNewChat = useCreateNewChat();

  return useAtomCallback(
    useCallback(
      async (get, set, conversationId: number) => {
        try {
          await deleteAgentConversation(conversationId);
          if (get(ChatConversationIdAtom) === conversationId) {
            onCreateNewChat({ closeMenu: false });
          }
          queryClient.invalidateQueries({ queryKey: [ChatConversationsQueryKey] });
        } catch (error) {
          console.error(error);
          pushErrorToast({ message: getErrorMessage(error, 'Failed to delete conversation') });
        }
      },
      [onCreateNewChat, pushErrorToast]
    )
  );
}

export function useCreateNewChat() {
  const setShowChatMenu = useSetAtom(ShowChatMenuAtom);
  const setConversationId = useSetAtom(ChatConversationIdAtom);
  const setMessages = useSetAtom(ChatMessagesAtom);
  const setSourcingSearchPrompt = useSetAtom(sourcingSearchPromptAtom);
  const setAgentActivity = useSetAtom(AgentActivityAtom);
  return useCallback(
    ({ closeMenu = true }: { closeMenu?: boolean } = {}) => {
      setMessages([]);
      setConversationId(undefined);
      setSourcingSearchPrompt(null);
      // Activity frames belong to the conversation they arrived in — don't let
      // a stale label bleed into the fresh chat.
      setAgentActivity({});
      if (closeMenu) {
        setShowChatMenu(false);
      }
      // there might be a new conversation that needs to be added to the list
      queryClient.invalidateQueries({ queryKey: [ChatConversationsQueryKey] });
    },
    [setConversationId, setMessages, setSourcingSearchPrompt, setAgentActivity, setShowChatMenu]
  );
}

/** Consume SeedChatPromptAtom: send it as the opening message, but only into a
 *  brand-new conversation — an existing conversation (or loaded history) means
 *  the user is mid-thought and an injected question would hijack it. The seed
 *  is cleared on first sight either way, so it can never fire twice or
 *  resurrect into a later chat. */
export function useConsumeSeedChatPrompt(sendMessage: (content: string) => void) {
  const seed = useAtomValue(SeedChatPromptAtom);
  const consume = useAtomCallback(
    useCallback(
      (get, set) => {
        const pending = get(SeedChatPromptAtom);
        if (!pending) {
          return;
        }
        set(SeedChatPromptAtom, null);
        if (get(ChatConversationIdAtom) !== undefined || get(ChatMessagesAtom).length > 0) {
          return;
        }
        sendMessage(pending);
      },
      [sendMessage]
    )
  );
  useEffect(() => {
    if (seed) {
      consume();
    }
  }, [seed, consume]);
}

export function useCurrentConversation() {
  const getConversationMessages = useGetConversation();

  const onSelectConversation = useAtomCallback(
    useCallback(
      async (get, set, conversationId: number) => {
        set(ChatConversationIdAtom, conversationId);
        // Drop any activity left over from the previously shown conversation.
        set(AgentActivityAtom, {});
        const messages = (await getConversationMessages(conversationId)) ?? [];
        set(
          ChatMessagesAtom,
          messages.map((message) => ({
            ...message,
            id: message.id,
            clientKey: String(message.id),
            content: message.content,
            feedback: message.feedback,
            isUser: message.role === 'user',
            // Out-of-band marker on event-turn replies and legacy digests
            // (FD-4182) — drives the anti-fuse gap and forced heading. The
            // history endpoint doesn't expose additional_kwargs yet, so this
            // lights up once the agent's MessageOut serializes it.
            source: message.additional_kwargs?.source ?? undefined,
            type:
              message.role === 'user' ? ChatSocketMessageType.chat : ChatSocketMessageType.aiResponseComplete,
          }))
        );
        set(ShowChatMenuAtom, false);
        queryClient.invalidateQueries({ queryKey: [ChatConversationsQueryKey] });
      },
      [getConversationMessages]
    )
  );

  return {
    onSelectConversation,
  };
}

export function useResetChatState() {
  return useAtomCallback(
    useCallback((get, set) => {
      // keep selected conversation id if set
      set(ShowChatMenuAtom, false);
      set(IsReceivingMessagesAtom, false);
      set(IsCancellingChatGenerationAtom, false);
      set(ChatErrorStateAtom, null);
      set(AgentActivityAtom, {});
      // This reset runs on chat teardown (right after the socket is
      // disconnected in useWsChat's unmount cleanup), never on reconnects —
      // so the identity survives reconnects for the tenant-mismatch
      // comparison, but a dead socket's identity can't produce a stale
      // mismatch banner when the chat remounts later.
      set(AgentWsIdentityAtom, null);
    }, [])
  );
}
